// components/Charts/AnalyticsPage.jsx — with fullscreen zoom per chart
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Text, Tab, TabList, Badge } from "@fluentui/react-components";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useSchema } from "../../context/SchemaContext";
import { formatDate, safeNum } from "../../utils/helpers";
import DateRangeFilter from "../shared/DateRangeFilter";

const CHART_COLORS = [
  "#0052CC","#107C10","#D83B01","#5C2D91",
  "#008272","#986F0B","#D13438","#0078D4",
  "#00B7C3","#498205","#CA5010","#8764B8",
];

// ─── Tooltip ────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:10, padding:"10px 14px", boxShadow:"0 8px 24px rgba(0,0,0,0.12)", maxWidth:260 }}>
      <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:7, fontWeight:600 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:3 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:p.color, flexShrink:0 }} />
          <span style={{ fontSize:12, color:"#374151", flex:1 }}>{p.name}:</span>
          <span style={{ fontSize:12, fontWeight:700, color:"#111827" }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Chart renderer — reused for both normal + fullscreen ───────────────────
function renderChart(entity, data, color, idx, height = 220) {
  const ax = { fontSize: 11, fill: "#8A8886" };
  const common = { data, margin: { top: 5, right: 20, left: 0, bottom: 50 } };
  const key = entity.key;
  const name = entity.name;

  if (entity.chartType === "bar") return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart {...common}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="week" tick={{ ...ax, angle: -40, textAnchor: "end" }} interval={0} />
        <YAxis tick={ax} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey={key} name={name} fill={color} radius={[5, 5, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  if (entity.chartType === "area") {
    const gid = `agz_${key}_${idx}`;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart {...common}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.28} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis dataKey="week" tick={{ ...ax, angle: -40, textAnchor: "end" }} interval={0} />
          <YAxis tick={ax} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey={key} name={name} stroke={color} fill={`url(#${gid})`} strokeWidth={2.5} dot={{ r: 3, fill: color }} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart {...common}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="week" tick={{ ...ax, angle: -40, textAnchor: "end" }} interval={0} />
        <YAxis tick={ax} />
        <Tooltip content={<CustomTooltip />} />
        <Line type="monotone" dataKey={key} name={name} stroke={color} strokeWidth={2.5} dot={{ r: 3, fill: color }} activeDot={{ r: 6 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function renderMultiLine(entities, data, height = 300) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#8A8886", angle: -40, textAnchor: "end" }} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: "#8A8886" }} />
        <Tooltip content={<CustomTooltip />} />
        <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
        {entities.map((e, i) => (
          <Line key={e.id} type="monotone" dataKey={e.key} name={e.name}
            stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.5}
            dot={{ r: 3 }} activeDot={{ r: 6 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Fullscreen Modal ────────────────────────────────────────────────────────
function FullscreenModal({ chart, onClose }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!chart) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,10,30,0.72)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, animation: "fsIn 0.18s ease",
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes fsIn { from { opacity:0; transform:scale(0.96) } to { opacity:1; transform:scale(1) } }
        @keyframes fsOut { from { opacity:1 } to { opacity:0 } }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20,
          width: "100%", maxWidth: 1100,
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          boxShadow: "0 40px 100px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: "18px 24px", borderBottom: "1px solid #F3F4F6",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(135deg,#001A3D,#002D7A)", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {chart.icon && (
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                {chart.icon}
              </div>
            )}
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{chart.title}</div>
              {chart.badge && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{chart.badge}</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>Press Esc to close</div>
            <button
              onClick={onClose}
              style={{
                width: 36, height: 36, borderRadius: 10,
                border: "1.5px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff", fontSize: 20, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Chart area — tall & full width */}
        <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto", minHeight: 0 }}>
          {chart.content}
        </div>

        {/* Footer hint */}
        <div style={{ padding: "12px 24px", borderTop: "1px solid #F3F4F6", background: "#FAFBFC", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 20px", borderRadius: 9,
              border: "1.5px solid #E5E7EB", background: "#fff",
              color: "#374151", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F9FAFB"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
          >
            Close Fullscreen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Chart Card with zoom button ─────────────────────────────────────────────
function ChartCard({ title, badge, icon, color, onZoom, children }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: "#fff", borderRadius: 16, padding: 24,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #EDEBE9",
        position: "relative", transition: "box-shadow 0.2s",
        ...(hovered ? { boxShadow: "0 6px 28px rgba(0,82,204,0.12)", borderColor: "#C7E0FF" } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Card header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        {icon && (
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: color ? color : "linear-gradient(135deg,#0052CC,#003DA6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, flexShrink: 0,
          }}>
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
          {badge && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 1 }}>{badge}</div>}
        </div>

        {/* ── ZOOM BUTTON ── */}
        <button
          onClick={onZoom}
          title="View fullscreen"
          style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            border: `1.5px solid ${hovered ? "#C7E0FF" : "#E5E7EB"}`,
            background: hovered ? "#EBF3FF" : "#F9FAFB",
            color: hovered ? "#0052CC" : "#9CA3AF",
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", transition: "all 0.15s", fontSize: 15,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#0052CC"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#0052CC"; e.currentTarget.style.transform = "scale(1.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = hovered ? "#EBF3FF" : "#F9FAFB"; e.currentTarget.style.color = hovered ? "#0052CC" : "#9CA3AF"; e.currentTarget.style.borderColor = hovered ? "#C7E0FF" : "#E5E7EB"; e.currentTarget.style.transform = "scale(1)"; }}
        >
          ⛶
        </button>
      </div>

      {children}
    </div>
  );
}

// ─── Diesel per-location charts ─────────────────────────────────────────────
const DIESEL_METRICS = [
  { key: "closingStock",  label: "Closing Stock (L)",  color: "#0052CC" },
  { key: "openingStock",  label: "Opening Stock (L)",  color: "#107C10" },
  { key: "supply",        label: "Supply (L)",         color: "#008272" },
  { key: "consumption",   label: "Consumption (L)",    color: "#D83B01" },
];

function DieselLocationCharts({ dieselTransactions, filteredWeeks, onZoom }) {
  const byLocation = useMemo(() => {
    const visibleWeeks = new Set((filteredWeeks || []).map(r => r.weekEnding));
    const filtered = visibleWeeks.size
      ? dieselTransactions.filter(r => visibleWeeks.has(r.weekEnding))
      : dieselTransactions;
    const map = {};
    filtered.forEach(r => {
      const loc = r.location || "Unknown";
      if (!map[loc]) map[loc] = [];
      map[loc].push(r);
    });
    return Object.entries(map).map(([location, recs]) => ({
      location,
      data: [...recs]
        .sort((a, b) => a.weekEnding.localeCompare(b.weekEnding))
        .map(r => ({
          week: r.weekEnding,
          closingStock:  r.closingStock  ?? 0,
          openingStock:  r.openingStock  ?? 0,
          supply:        r.supply        ?? 0,
          consumption:   r.consumption   ?? 0,
        })),
    }));
  }, [dieselTransactions, filteredWeeks]);

  if (!byLocation.length) return null;
  const ax = { fontSize: 11, fill: "#8A8886" };

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg,#986F0B,#D4921A)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>⛽</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Diesel by Location</div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{byLocation.length} location{byLocation.length !== 1 ? "s" : ""} · stock trends</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 20 }}>
        {byLocation.map(({ location, data }) => {
          const chartContent = (height) => (
            <ResponsiveContainer width="100%" height={height}>
              <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="week" tick={{ ...ax, angle: -40, textAnchor: "end" }} interval={0} />
                <YAxis tick={ax} />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                {DIESEL_METRICS.map(m => (
                  <Line key={m.key} type="monotone" dataKey={m.key} name={m.label}
                    stroke={m.color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          );
          return (
            <ChartCard
              key={location}
              title={location}
              badge="Opening · Supply · Consumption · Closing"
              icon="⛽"
              color="linear-gradient(135deg,#986F0B,#D4921A)"
              onZoom={() => onZoom(location, `Diesel — ${location}`, "⛽", chartContent(480))}
            >
              {chartContent(220)}
            </ChartCard>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function AnalyticsPage({ dateFilter, dieselTransactions = [] }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const records = useMemo(() => dateFilter?.filtered ?? [], [dateFilter?.filtered]);
  const { chartEntities, categories } = useSchema();
  const [activeTab, setActiveTab] = useState(null);
  const [fullscreen, setFullscreen] = useState(null); // { title, badge, icon, content }

  const chartCategories = useMemo(() =>
    categories.filter(c => chartEntities.some(e => e.category === c)),
    [categories, chartEntities]
  );

  const currentTab = activeTab || chartCategories[0] || "";

  const chartData = useMemo(() =>
    [...records].reverse().map(r => {
      const row = { week: formatDate(r.weekEnding) };
      chartEntities.forEach(e => { row[e.key] = safeNum(r.fields?.[e.key]); });
      return row;
    }),
    [records, chartEntities]
  );

  const categoryEntities = useMemo(() =>
    chartEntities.filter(e => e.category === currentTab),
    [chartEntities, currentTab]
  );

  const openZoom = useCallback((title, badge, icon, content) => {
    setFullscreen({ title, badge, icon, content });
  }, []);

  const closeZoom = useCallback(() => setFullscreen(null), []);

  if (chartCategories.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
        <Text size={500} style={{ color: "#374151", fontWeight: 700, display: "block" }}>No chart entities active</Text>
        <Text style={{ color: "#9CA3AF", marginTop: 6, display: "block" }}>Enable entities in Entity Manager and mark "Show in Charts".</Text>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Text weight="bold" size={600} style={{ color: "#201F1E", display: "block" }}>Analytics & Trends</Text>
          <Text style={{ color: "#9CA3AF", fontSize: 13 }}>
            {chartEntities.length} metrics · {chartCategories.length} categories · {records.length} weeks
            {dateFilter?.rangeLabel ? ` · ${dateFilter.rangeLabel}` : ""} · click ⛶ to expand
          </Text>
        </div>
      </div>

      {/* Date Range Filter — shared with Dashboard */}
      {dateFilter && <DateRangeFilter {...dateFilter} style={{ marginBottom: 24 }} />}

      {/* Tabs */}
      <TabList selectedValue={currentTab} onTabSelect={(_, d) => setActiveTab(d.value)} style={{ marginBottom: 24 }}>
        {chartCategories.map(cat => (
          <Tab key={cat} value={cat}>
            {cat}
            <Badge appearance="tint" style={{ marginLeft: 6, fontSize: 10 }}>
              {chartEntities.filter(e => e.category === cat).length}
            </Badge>
          </Tab>
        ))}
      </TabList>

      {/* Multi-series overview card */}
      {categoryEntities.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <ChartCard
            title={`${currentTab} — All Metrics`}
            badge={`${categoryEntities.length} series · multi-line overview`}
            icon="📈"
            onZoom={() => openZoom(
              `${currentTab} — All Metrics`,
              `${categoryEntities.length} series overview`,
              "📈",
              renderMultiLine(categoryEntities, chartData, 480)
            )}
          >
            {renderMultiLine(categoryEntities, chartData, 300)}
          </ChartCard>
        </div>
      )}

      {/* Individual charts grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 20 }}>
        {categoryEntities.map((entity, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          return (
            <ChartCard
              key={entity.id}
              title={entity.name}
              badge={`${entity.type}${entity.unit ? " · " + entity.unit : ""}`}
              icon={entity.icon}
              color={`linear-gradient(135deg,${color},${color}cc)`}
              onZoom={() => openZoom(
                entity.name,
                `${entity.type}${entity.unit ? " · " + entity.unit : ""} · ${records.length} weeks`,
                entity.icon,
                renderChart(entity, chartData, color, i, 480)
              )}
            >
              {renderChart(entity, chartData, color, i, 220)}
            </ChartCard>
          );
        })}
      </div>

      {categoryEntities.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 48, textAlign: "center", border: "1px solid #EDEBE9" }}>
          <Text style={{ color: "#9CA3AF" }}>No chart entities in this category.</Text>
        </div>
      )}

      {/* Diesel per-location charts — shown on the Diesel Inventory tab */}
      {currentTab === "Diesel Inventory" && dieselTransactions.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <DieselLocationCharts
            dieselTransactions={dieselTransactions}
            filteredWeeks={records}
            onZoom={(badge, title, icon, content) => openZoom(title, badge, icon, content)}
          />
        </div>
      )}

      {/* Fullscreen Modal */}
      <FullscreenModal chart={fullscreen} onClose={closeZoom} />
    </div>
  );
}